import { HttpClient } from '@angular/common/http';
import { inject, Injectable, Injector } from '@angular/core';
import { Translation, TranslocoService } from '@jsverse/transloco';
import { catchError, firstValueFrom, Observable, of, tap } from 'rxjs';

import { ENGLISH_LANGUAGE_CODE } from '../../../models/language.constants';
import { TranslationErrorHandler } from '../translation-error-handler';
import { TranslationUrlBuilder } from '../translation-url-builder';
import { TranslationLoadingStrategy } from './translation-loading-strategy.interface';

@Injectable({ providedIn: 'root' })
export class BlobTranslationStrategy implements TranslationLoadingStrategy {
  private http = inject(HttpClient);
  private urlBuilder = inject(TranslationUrlBuilder);
  private injector = inject(Injector);
  private translocoService?: TranslocoService;
  private englishLoaded = false;

  loadGlobal(lang: string): Observable<Translation> {
    if (lang !== ENGLISH_LANGUAGE_CODE && !this.englishLoaded) {
      this.loadEnglishTranslation();
    }

    return this.loadTranslationFromBlob('global', lang).pipe(
      catchError((error) => this.handleBlobError(lang, error)),
    );
  }

  async loadScoped(scope: string, lang: string): Promise<any> {
    const blobUrl = this.urlBuilder.buildBlobUrl(scope, lang);
    const headers = this.urlBuilder.getNoCacheHeaders();

    try {
      return await firstValueFrom(this.http.get(blobUrl, { headers }));
    } catch (error) {
      TranslationErrorHandler.handleBlobLoadError(scope, lang, error);
      throw error;
    }
  }

  private getTranslocoService(): TranslocoService {
    if (!this.translocoService) {
      this.translocoService = this.injector.get(TranslocoService);
    }

    return this.translocoService;
  }

  private loadTranslationFromBlob(
    scope: string,
    lang: string,
  ): Observable<Translation> {
    const url = this.urlBuilder.buildBlobUrl(scope, lang);
    const headers = this.urlBuilder.getNoCacheHeaders();

    return this.http.get<Translation>(url, { headers });
  }

  private loadTranslationFromAssets(lang: string): Observable<Translation> {
    const url = this.urlBuilder.buildAssetUrl(lang);

    return this.http.get<Translation>(url);
  }

  private handleBlobError(lang: string, error: any): Observable<Translation> {
    TranslationErrorHandler.logBlobFallbackWarning(lang, error);

    return this.loadTranslationFromAssets(ENGLISH_LANGUAGE_CODE).pipe(
      catchError((fallbackError) => {
        TranslationErrorHandler.handleFallbackError(
          ENGLISH_LANGUAGE_CODE,
          fallbackError,
        );

        return of({} as Translation);
      }),
    );
  }

  private loadEnglishTranslation(): void {
    this.englishLoaded = true;
    this.loadTranslationFromBlob('global', ENGLISH_LANGUAGE_CODE)
      .pipe(
        catchError(() => {
          TranslationErrorHandler.logEnglishLoadWarning();

          return this.loadTranslationFromAssets(ENGLISH_LANGUAGE_CODE).pipe(
            catchError((error) => {
              TranslationErrorHandler.logEnglishLoadError(error);

              return of({} as Translation);
            }),
          );
        }),
        tap((translation) => {
          this.getTranslocoService().setTranslation(
            translation,
            ENGLISH_LANGUAGE_CODE,
          );
        }),
      )
      .subscribe();
  }
}
